import React, { useState } from "react";
import { useLanguage } from "../context/LanguageContext";

import {
  Droplets,
  Leaf,
  MapPin,
  Waves,
  Mountain,
  TreePine,
  Warehouse,
  ChartNoAxesCombined,
  Trash,
  FlaskConical,
  Sprout,
  SquareMenu,
  HeartHandshake,
  FunnelPlus,
  Hourglass,
  LayersPlus,
  Wrench,
  Globe2,
  Settings2,
  HardHat
} from "lucide-react";

// ICON LOGIC
const getIcon = (title) => {
  const t = title.toLowerCase();

  if (
    t.includes("fluidos") ||
    t.includes("fluido") ||
    t.includes("perforación")
  ) {
    return FunnelPlus;
  }

  if (
    t.includes("control de sólidos") ||
    t.includes("control")
  ) {
    return LayersPlus;
  }

  if (
    t.includes("intervención") ||
    t.includes("completamiento") ||
    t.includes("workover")
  ) {
    return Wrench;
  }

  if (
    t.includes("cobertura") ||
    t.includes("nacional") ||
    t.includes("sede")
  ) {
    return Globe2;
  }

  if (
    t.includes("ingeniería") ||
    t.includes("optimización")
  ) {
    return Settings2;
  }

  if (
    t.includes("soporte") ||
    t.includes("campo")
  ) {
    return HardHat;
  }

  if (
    t.includes("tiempo") ||
    t.includes("tiempos")
  ) {
    return Hourglass;
  }

  if (
    t.includes("agua") ||
    t.includes("río") ||
    t.includes("ciénaga")
  ) {
    return Waves;
  }

  if (
    t.includes("impacto") ||
    t.includes("estudios de impacto")
  ) {
    return ChartNoAxesCombined;
  }

  if (
    t.includes("licencia") ||
    t.includes("licencias") ||
    t.includes("anla")
  ) {
    return SquareMenu;
  }

  if (
    t.includes("restauración") ||
    t.includes("ecológica")
  ) {
    return TreePine;
  }

  if (
    t.includes("laboratorio") ||
    t.includes("fisicoquímicos")
  ) {
    return FlaskConical;
  }

  if (
    t.includes("residuos") ||
    t.includes("residuo")
  ) {
    return Trash;
  }

  if (
    t.includes("cuenca") ||
    t.includes("ribereña")
  ) {
    return Mountain;
  }

  if (
    t.includes("consultoría") ||
    t.includes("consultoria") ||
    t.includes("ambiental")
  ) {
    return Leaf;
  }

  if (
    t.includes("influencia") ||
    t.includes("aid")
  ) {
    return Sprout;
  }

  if (
    t.includes("siembra") ||
    t.includes("vida")
  ) {
    return HeartHandshake;
  }

  return MapPin;
};

// THEME VISUAL
const theme = {
  oilAndGas: {
    color: "#ee7d32",
    badge: "bg-orange-100 text-orange-600",
    icon: "text-orange-500",
    title: "OPERACIONES OIL & GAS",
  },
  ambiental: {
    color: "#22c55e",
    badge: "bg-green-100 text-green-600",
    icon: "text-green-600",
    title: "OPERACIONES AMBIENTALES",
  },
};

// DATA
const operationsData = {
  oilAndGas: {
    title: "OIL & GAS",
    icon: <Droplets size={18} />,
    details: [
      {
        title: "Fluidos de Perforación",
        items: [
          "Sistemas base agua (VIM)",
          "Fluidos de alta densidad HPHT",
          "Ingeniería para optimización de ROP",
        ],
      },
      {
        title: "Control de Sólidos",
        items: [
          "Operación de equipos en campo",
          "Optimización y reducción de costos",
          "Monitoreo continuo de parámetros",
        ],
      },
      {
        title: "Intervención y Completamiento",
        items: [
          "Fluidos para workover",
          "Soluciones para control de formación",
          "Limpieza y terminación de pozos",
        ],
      },
      {
        title: "Cobertura Nacional",
        items: [
          "Operaciones en las principales cuencas petroleras de Colombia",
          "Presencia en regiones del centro, oriente y sur del país",
        ],
      },
      {
        title: "Ingeniería y Optimización",
        items: [
          "Diseño y evaluación de sistemas de fluidos",
          "Análisis de parámetros operacionales",
          "Optimización del desempeño de las operaciones",
        ],
      },
      {
        title: "Soporte Técnico en Campo",
        items: [
          "Acompañamiento técnico durante las operaciones",
          "Seguimiento y control de parámetros en campo",
          "Asistencia para la toma de decisiones operacionales",
        ],
      },
    ],
  },

  ambiental: {
    title: "AMBIENTAL",
    icon: <Leaf size={18} />,
    details: [
      {
        title: "Monitoreo de Agua",
        items: ["Río Magdalena", "Ciénaga Grande"],
      },
      {
        title: "Área de Influencia Directa (AID)",
        items: ["+150 puntos"],
      },

      {
        title: "Estudios de Impacto Ambiental",
        items: ["Proyecto Bloque Norte"],
      },
      {
        title: "Autoridad Nacional de Licencias Ambientales (ANLA)",
        items: ["Licencia Global"],
      },

      {
        title: "Restauración Ecológica",
        items: ["Zona Ribereña"],
      },

      {
        title: "Manejo de Residuos",
        items: ["Base Operativa Central"],
      },
      {
        title: "Laboratorio Ambiental",
        items: ["Análisis Fisicoquímicos", "IDEAM"],
      },

      {
        title: "Programa Siembra Vida",
        items: ["+10 Hectáreas"],
      },
      {
        title: "Consultoría Ambiental",
        items: ["Planes de Manejo", "Permisos y Licencias"],
      },

      {
        title: "Sede Bogotá",
        items: ["Alcance Nacional"],
      },
    ],
  },
};

// UBICACIONES
const locations = {
  ambiental: [
    { name: "Guajira", x: 52, y: 6 },
    { name: "Arauca", x: 63, y: 33 },
    { name: "Arauca", x: 68, y: 33 },
    { name: "Arauca", x: 60, y: 37 },
    { name: "Bolivar", x: 44, y: 20 },
    { name: "Bolivar", x: 46, y: 25 },
    { name: "Santander", x: 46, y: 31 },
    { name: "Santander", x: 49, y: 32 },
    { name: "Santander", x: 50, y: 35 },
    { name: "Boyacá", x: 51, y: 38 },
    { name: "Boyacá", x: 51, y: 42 },
    { name: "Casanare", x: 52, y: 46 },
    { name: "Casanare", x: 58, y: 42 },
    { name: "Casanare", x: 63, y: 41 },
    { name: "Cundinamarca", x: 41, y: 45 },
    { name: "Antioquia", x: 39, y: 41 },
    { name: "Vichada", x: 67, y: 44 },
    { name: "Vichada", x: 69, y: 48 },
    { name: "Vichada", x: 68, y: 51 },
    { name: "Meta", x: 60, y: 52 },
    { name: "Guaviare", x: 62, y: 56 },
    { name: "Tolima", x: 34, y: 50 },
    { name: "Huila", x: 34, y: 56 },
    { name: "Caquetá", x: 36, y: 60 },
    { name: "Caquetá", x: 34, y: 65 },
    { name: "Putumayo", x: 32, y: 72 },

    //BASES EMPRESA
    { name: "Base Casanare", x: 62, y: 44, type: "base" },
    { name: "Base Cundinamarca", x: 46, y: 45, type: "base" },
    { name: "Base Santander", x: 47, y: 34, type: "base" },
    { name: "Base Putumayo", x: 28, y: 71, type: "base" },
  ],
};

const locations1 = {
  oilAndGas: [
    { name: "Guajira", x: 54, y: 6 },
    { name: "Arauca", x: 63, y: 33 },
    { name: "Arauca", x: 68, y: 33 },
    { name: "Arauca", x: 60, y: 37 },
    { name: "Bolivar", x: 44, y: 20 },
    { name: "Bolivar", x: 46, y: 25 },
    { name: "Santander", x: 46, y: 31 },
    { name: "Santander", x: 49, y: 32 },
    { name: "Santander", x: 50, y: 35 },
    { name: "Boyacá", x: 51, y: 38 },
    { name: "Boyacá", x: 51, y: 42 },
    { name: "Casanare", x: 52, y: 46 },
    { name: "Casanare", x: 58, y: 42 },
    { name: "Casanare", x: 63, y: 41 },
    { name: "Cundinamarca", x: 41, y: 45 },
    { name: "Antioquia", x: 39, y: 41 },
    { name: "Vichada", x: 67, y: 44 },
    { name: "Vichada", x: 69, y: 48 },
    { name: "Vichada", x: 68, y: 51 },
    { name: "Meta", x: 60, y: 52 },
    { name: "Guaviare", x: 62, y: 56 },
    { name: "Tolima", x: 34, y: 50 },
    { name: "Huila", x: 34, y: 56 },
    { name: "Caquetá", x: 36, y: 60 },
    { name: "Caquetá", x: 34, y: 65 },
    { name: "Putumayo", x: 33, y: 71 },
  ],
};

// COMPONENT
const Operations = () => {
  useLanguage();
  const [activeTab, setActiveTab] = useState("oilAndGas");

  const current = operationsData[activeTab];
  const currentTheme = theme[activeTab];

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#f6f7f9] via-[#f7f7f7] to-[#ffffff]">
      <section className="py-16">
        <div className="max-w-[1450px] mx-auto px-6">
          {/* HEADER */}
          <div className="flex flex-col xl:flex-row xl:justify-between gap-10 mb-12">
            <div>
              <h1 className="text-[52px] font-black text-[#111] tracking-[-2px]">
                Operaciones
              </h1>

              <div className="w-20 h-[4px] bg-gradient-to-r from-[#ee7d32] to-orange-400 mt-4 mb-5 rounded-full" />

              <p className="text-gray-600 max-w-[600px] leading-[1.7]">
                Soluciones técnicas y ambientales que generan valor sostenible
                en todo el territorio
              </p>
            </div>

            {/* BOTONES */}
            <div className="flex flex-wrap gap-3">
              {Object.entries(operationsData).map(([key, val]) => {
                const isActive = activeTab === key;

                return (
                  <button
                    key={key}
                    onClick={() => setActiveTab(key)}
                    className={`h-[50px] px-6 text-sm font-semibold flex items-center gap-2 rounded-xl transition-all border backdrop-blur-md ${
                      isActive
                        ? "bg-gradient-to-r from-[#ee7d32] to-orange-500 text-white shadow-lg scale-[1.02]"
                        : "bg-white/80 text-gray-700 border-gray-200 hover:border-[#ee7d32] hover:shadow-md"
                    }`}
                  >
                    {React.cloneElement(val.icon, {
                      size: 18,
                      stroke: isActive ? "white" : "#6b7280",
                    })}

                    {val.title}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="grid xl:grid-cols-[1.2fr_0.8fr] gap-10">
            <div className="rounded-[28px] bg-white/70 backdrop-blur-xl border border-white shadow-xl p-6">
              <span
                className={`text-xs font-bold px-3 py-1 rounded-full ${currentTheme.badge}`}
              >
                {currentTheme.title}
              </span>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mt-4">
                {current.details.map((item, i) => {
                  const Icon = getIcon(item.title);

                  return (
                    <div
                      key={i}
                      className="group rounded-2xl border border-gray-100 bg-white/60 p-5 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all"
                    >
                      <div className="flex items-start gap-3">
                        <div className="w-11 h-11 rounded-xl bg-white flex items-center justify-center">
                          <Icon size={16} className={currentTheme.icon} />
                        </div>

                        <div>
                          <h3 className="text-[14px] font-bold text-[#111]">
                            {item.title}
                          </h3>

                          <ul className="text-gray-500 text-[13px] mt-2 space-y-1">
                            {item.items.map((text, idx) => (
                              <li key={idx}>{text}</li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* MAPA */}
            <div className="rounded-[28px] border shadow-xl bg-white overflow-hidden">
              <div className="relative w-full aspect-[768/891] max-w-[650px] mx-auto">
                <img
                  src="/Images/operations/bases-map_01.webp"
                  alt="Mapa Base"
                  className="absolute inset-0 w-full h-full"
                />
                {/* PUNTOS OIL & GAS */}
                {activeTab === "oilAndGas" &&
                  locations1.oilAndGas.map((loc, i) => (
                    <div
                      key={i}
                      className="absolute group z-20 hover:z-[9999]"
                      style={{
                        left: `${loc.x}%`,
                        top: `${loc.y}%`,
                        transform: "translate(-50%, -50%)",
                      }}
                    >
                      <div className="relative">
                        {loc.type === "base" ? (
                          <Warehouse />
                        ) : (
                          <MapPin color="#ee7d31" />
                        )}

                        <div className="absolute top-7 left-1/2 -translate-x-1/2 z-[99999] opacity-0 group-hover:opacity-100 transition-all duration-200 pointer-events-none">
                          <div className="bg-black text-white text-[10px] px-2 py-[3px] rounded whitespace-nowrap shadow-xl">
                            {loc.name}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}

                {/* PUNTOS AMBIENTALES */}
                {activeTab === "ambiental" &&
                  locations.ambiental.map((loc, i) => (
                    <div
                      key={i}
                      className="absolute group z-20 hover:z-[9999]"
                      style={{
                        left: `${loc.x}%`,
                        top: `${loc.y}%`,
                        transform: "translate(-50%, -50%)",
                      }}
                    >
                      <div className="relative">
                        {loc.type === "base" ? (
                          <Warehouse />
                        ) : (
                          <MapPin color="#18a34b" />
                        )}

                        <div className="absolute top-7 left-1/2 -translate-x-1/2 z-[99999] opacity-0 group-hover:opacity-100 transition-all duration-200 pointer-events-none">
                          <div className="bg-black text-white text-[10px] px-2 py-[3px] rounded whitespace-nowrap shadow-xl">
                            {loc.name}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          </div>

          {/* CTA */}
          <div className="mt-6 flex justify-end">
            <a
              href="/contact-us"
              className="group flex items-center gap-2 text-sm font-medium text-[#ee7d32] hover:text-orange-600 transition"
            >
              ¿Necesitas más información?
              <span className="w-6 h-[2px] bg-[#ee7d32] group-hover:w-10 transition-all" />
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Operations;
